import { useCallback, useEffect, useState } from 'react';
import { useKioskStore } from '@/store/kiosk-store';
import { ApiClient } from '@/lib/api-client';
import type { HandoffResponse } from '@museum-kiosk/shared-types';

const client = new ApiClient();

export interface HandoffMeta {
  icon?: string;
  label?: string;
  color?: string;
}

export interface UseHandoff {
  createHandoff(moduleUrl: string, meta?: HandoffMeta): Promise<void>;
  isLoading: boolean;
  loadingUrl: string | null;
}

function buildRedirectUrl(moduleUrl: string, token: string, meta?: HandoffMeta): string {
  try {
    const u = new URL(moduleUrl);
    u.searchParams.set('kiosk_token', token);
    if (meta?.label) u.searchParams.set('kiosk_label', meta.label);
    if (meta?.icon) u.searchParams.set('kiosk_icon', meta.icon);
    if (meta?.color) u.searchParams.set('kiosk_color', meta.color);
    return u.toString();
  } catch {
    const sep = moduleUrl.includes('?') ? '&' : '?';
    const extras = [
      `kiosk_token=${encodeURIComponent(token)}`,
      meta?.label ? `kiosk_label=${encodeURIComponent(meta.label)}` : null,
      meta?.icon ? `kiosk_icon=${encodeURIComponent(meta.icon)}` : null,
      meta?.color ? `kiosk_color=${encodeURIComponent(meta.color)}` : null,
    ]
      .filter(Boolean)
      .join('&');
    return `${moduleUrl}${sep}${extras}`;
  }
}

/**
 * Creates a handoff token and navigates to the module URL.
 * On error, resets the kiosk to the attract screen.
 */
export function useHandoff(): UseHandoff {
  const reset = useKioskStore((s) => s.reset);
  const endSession = useKioskStore((s) => s.endSession);
  const [loadingUrl, setLoadingUrl] = useState<string | null>(null);

  // Clear loading when the user comes back to the launcher via browser back
  // (bfcache restore) — e.g. module page failed to load. Without this, the
  // restored React state still has loadingUrl set and the overlay sticks.
  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent): void => {
      if (e.persisted) setLoadingUrl(null);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const createHandoff = useCallback(
    async (moduleUrl: string, meta?: HandoffMeta) => {
      setLoadingUrl(moduleUrl);
      try {
        const [data] = await Promise.all([
          client.post<HandoffResponse>('/api/auth/handoff', {
            module_url: moduleUrl,
            return_url: window.location.origin,
            session_id: useKioskStore.getState().sessionId,
          }),
        ]);
        window.location.assign(buildRedirectUrl(moduleUrl, data.token, meta));
      } catch {
        setLoadingUrl(null);
        endSession();
        reset();
      }
    },
    [reset, endSession],
  );

  return { createHandoff, isLoading: loadingUrl !== null, loadingUrl };
}
